/**
 * stageProgressHelpers.js
 *
 * Universal functions for managing stage transitions in Trust Issues.
 * Works for any stage (1, 2, 3...) by dynamically referencing Firestore fields:
 *  - ContinueClicks{stage}
 */

import { doc, updateDoc, arrayUnion, onSnapshot } from "firebase/firestore";
import { TrustIssuesRoomCollection } from "../../../assets/DatabaseCollections";
import { db } from "../../../config/firebase";
import { assignImpostorGameTeams } from "./impostorWordGameHelpers";

/**
 * Called when a player presses Continue.
 * Adds their userID to the correct ContinueClicks{stage} array.
 *
 * @param {string} roomID     - The ID of the game room.
 * @param {Object} user       - The current user object.
 * @param {number} stage      - Current stage number.
 */
export const handleContinueClick = async (roomID, user, stage) => {
    const fieldName = `ContinueClicks${stage}`;
    try {
        const roomRef = doc(db, TrustIssuesRoomCollection, roomID);

        await updateDoc(roomRef, {
            [fieldName]: arrayUnion(user.uid),
        });
    } catch (error) {
        console.error(`[Stage ${stage}] Error updating ${fieldName}:`, error);
    }
};

/**
 * Listens for all players clicking Continue.
 * When everyone clicks, advances the stage by +1.
 *
 * @param {string} roomID           - The room ID to listen on.
 * @param {number} totalPlayers     - Total players in this game room.
 * @param {number} stage            - Current stage number.
 * @param {Function} setStage       - Function to update local stage state.
 */
export const listenForAllReady = (roomID, totalPlayers, stage, setStage) => {
    const roomRef = doc(db, TrustIssuesRoomCollection, roomID);

    return onSnapshot(roomRef, async (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();

        const fieldName = `ContinueClicks${stage}`;
        const clickCount = data?.[fieldName]?.length || 0;
        const currentStage = data?.stage;

        // 🧠 Prevent duplicate trigger on refresh
        if (currentStage > stage) {
            setStage(currentStage);
            return;
        }

        // ✅ Only advance if everyone is ready AND Firestore is still on this stage
        if (clickCount >= totalPlayers && currentStage === stage) {
            try {
                const endTimestamp = getNextTimer(stage);

                await updateDoc(roomRef, {
                    stage: stage + 1,
                    finishAt: arrayUnion(endTimestamp),
                });

                setStage(stage + 1);

                if (stage === 1) {
                    await assignImpostorGameTeams(roomID);
                }
            } catch (error) {
                console.error(`[Stage ${stage}] Error advancing stage:`, error);
            }
        }
    });
};

/**
 * Gets the next timer time
 * @param {*} roomID 
 * @param {*} stage 
 * @param {*} setStage 
 */
const getNextTimer = (stage) => {
    const durationMinutes =
        stage === 2 ? 10 :       // Impostor Word Game
        stage === 3 ? 10 :
        stage === 4 ? 15 :
        stage === 5 ? 10 : 
        stage === 7 ? 15 :
        10;
    return new Date(Date.now() + durationMinutes * 60 * 1000);
}

/**
 * 🚀 Advance to next stage.
 */
export const advanceStage = async (roomID, stage, setStage) => {
    const roomRef = doc(db, TrustIssuesRoomCollection, roomID);
    await updateDoc(roomRef, { 
        stage       :   stage + 1,
        finishAt    :   arrayUnion(getNextTimer(stage+1))
    });
    setStage(stage + 1);
};